import {
  Activity,
  Bell,
  ChartColumn,
  Coins,
  Factory,
  Headset,
  LayoutDashboard,
  Menu,
  Settings,
  Sun,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { UI_TEXT } from '@/shared/languages';
import { ROUTES } from '@/shared/routing';

/**
 * 앱 셸 네비게이션 SSOT — 데스크탑 icon-rail(`AppSidebar`)·모바일 하단 탭(`AppBottomNav`)이 **같은 config** 를
 * 소비(렌더만 다름, 정의·활성판정 중복 금지). 라벨=`UI_TEXT`, 경로=`ROUTES`, 아이콘=lucide. 1-depth 중첩.
 * 모바일: 일부만 하단 탭(`BOTTOM_TAB_KEYS` — 그룹 자식 key 도 가능) + "메뉴" 탭이 **heritage Sidebar 모바일 sheet**(=AppSidebar)를 연다.
 * 데스크탑: 사이드바가 전 항목 직접 노출.
 */

export interface NavLeaf {
  key: string;
  label: string;
  icon: LucideIcon;
  route: string;
  /** 하단 탭용 짧은 라벨(좁은 탭 폭). 없으면 `label`. 사이드바·드로어는 항상 `label`, `aria-label`도 `label`. */
  shortLabel?: string;
}

export interface NavGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  /** 자식 — 그룹 자체 경로 없음(펼침/섹션 라벨 역할). 자식도 leaf(아이콘 보유 — 하단 탭·드로어가 자식을 직접 쓸 수 있게). */
  children: NavLeaf[];
}

export type NavItem = NavLeaf | NavGroup;

export function isNavGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

export const NAV_ITEMS: NavItem[] = [
  {
    key: 'dashboard',
    label: UI_TEXT.NAV_DASHBOARD,
    icon: LayoutDashboard,
    route: ROUTES.DASHBOARD,
  },
  {
    key: 'stats',
    label: UI_TEXT.NAV_STATS,
    icon: ChartColumn,
    children: [
      {
        key: 'stats-generation',
        label: UI_TEXT.NAV_STATS_GENERATION,
        shortLabel: UI_TEXT.TERM_GENERATION,
        icon: Sun,
        route: ROUTES.STATS_GENERATION,
      },
      {
        key: 'stats-inverter',
        label: UI_TEXT.NAV_STATS_INVERTER,
        shortLabel: UI_TEXT.TERM_INVERTER,
        icon: Activity,
        route: ROUTES.STATS_INVERTER,
      },
    ],
  },
  { key: 'revenue', label: UI_TEXT.NAV_REVENUE, icon: Coins, route: ROUTES.REVENUE },
  { key: 'alerts', label: UI_TEXT.NAV_ALERTS, icon: Bell, route: ROUTES.ALERTS },
  // 설비정보 — 발전소 목록/상세 단일 메뉴(하위 없음). 구 '마이페이지'는 설정 '내정보' 탭으로 흡수됐다.
  { key: 'info', label: UI_TEXT.NAV_INFO, icon: Factory, route: ROUTES.PLANTS },
  { key: 'settings', label: UI_TEXT.NAV_SETTINGS, icon: Settings, route: ROUTES.SETTINGS },
  { key: 'support', label: UI_TEXT.NAV_SUPPORT, icon: Headset, route: ROUTES.SUPPORT },
];

/** 현재 경로가 이 라우트에 해당하는가 — 자신 또는 하위(`/route/...`). 대시보드가 `/dashboard` 실경로라 예전
 * HOME('/') 특례(모든 경로가 '/' 로 시작하는 오탐 방지)는 불필요해졌다. */
export function isRouteActive(route: string, pathname: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

/** 그룹 내 자식 중 활성 라우트가 있는가 — 그룹 자동 펼침·하이라이트 기준. */
export function isNavGroupActive(group: NavGroup, pathname: string): boolean {
  return group.children.some((child) => isRouteActive(child.route, pathname));
}

// === 모바일 하단 탭 (데스크탑은 사이드바가 NAV_ITEMS 전체를 직접 노출하므로 미사용) ===

/**
 * 모바일 하단 탭에 노출할 키(순서 = 좌→우). 최상위 leaf 뿐 아니라 **그룹 자식 key 도 가능**(발전량 조회·인버터 상세분석).
 * 6번째 탭 = "메뉴"(라우트 아님 — 드로어 토글, `MENU_TAB`). 여기 없는 항목도 드로어엔 전부 나온다(드로어=전체 네비).
 */
export const BOTTOM_TAB_KEYS: string[] = [
  'dashboard',
  'stats-generation',
  'stats-inverter',
  'revenue',
  'alerts',
];

/** "메뉴" 탭 — 라우트가 아니라 **드로어를 여는 액션**(전체 네비 오버레이). 하단 탭 끝에 버튼으로 렌더. */
export const MENU_TAB: Pick<NavLeaf, 'key' | 'label' | 'icon'> = {
  key: 'menu',
  label: UI_TEXT.NAV_MENU,
  icon: Menu,
};

/** key 로 nav leaf 를 찾는다(최상위 leaf 또는 그룹 자식 — 하단 탭이 자식 key 도 가리킬 수 있어 평탄 탐색). */
function findNavLeaf(key: string): NavLeaf | undefined {
  for (const item of NAV_ITEMS) {
    if (isNavGroup(item)) {
      const child = item.children.find((c) => c.key === key);
      if (child) return child;
    } else if (item.key === key) {
      return item;
    }
  }
  return undefined;
}

/** 하단 탭 leaf 항목(BOTTOM_TAB_KEYS 순서). "메뉴"(액션)는 `AppBottomNav` 가 끝에 별도 버튼으로 붙인다. */
export function bottomTabItems(): NavLeaf[] {
  return BOTTOM_TAB_KEYS.map(findNavLeaf).filter((tab): tab is NavLeaf => Boolean(tab));
}
